import { computed, ref } from 'vue'
import api from '@/services/api'
import type { LiveOpsLeaderboard } from '@/types/live-ops'
import { useNow } from '@/composables/game/time'
import { formatDuration } from '@/helpers/date'
import {
  type Challenge,
  CHALLENGE_PRIZE_TYPE,
  type PrizePoolAmounts,
} from '@/types/shared/player-config'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { liveOpsEventTemplates } from '@/composables/game/live-ops-events'

export const leaderboards = ref<LiveOpsLeaderboard[]>([])

const CURRENCY_TO_PRIZE_TYPE: Record<string, CHALLENGE_PRIZE_TYPE> = {
  TON: CHALLENGE_PRIZE_TYPE.TON,
  USDT: CHALLENGE_PRIZE_TYPE.USDT,
  DROPEE: CHALLENGE_PRIZE_TYPE.DROPEE,
}

function resolvePrizeType(currency: string): CHALLENGE_PRIZE_TYPE {
  const upperCurrency = currency.toUpperCase()
  return CURRENCY_TO_PRIZE_TYPE[upperCurrency]
    ?? (currency in allSpecialCurrenciesConfig.value
      ? CHALLENGE_PRIZE_TYPE.SPECIAL_CURRENCY
      : CHALLENGE_PRIZE_TYPE.TON)
}

function mapPrizePoolToChallengeFormat(leaderboard: LiveOpsLeaderboard): Challenge {
  const prizePool = leaderboard.prizePool
  if (!prizePool.length) return null

  const overallType = resolvePrizeType(prizePool[0].prize.currency)

  const amounts: PrizePoolAmounts = prizePool.map(entry => {
    const entryType = resolvePrizeType(entry.prize.currency)

    if (entryType === CHALLENGE_PRIZE_TYPE.SPECIAL_CURRENCY) {
      return {
        fromRank: entry.fromRank,
        toRank: entry.toRank,
        amount: entry.prize.amount,
        specialCurrencyId: entry.prize.currency as never,
      }
    }
    return {
      fromRank: entry.fromRank,
      toRank: entry.toRank,
      amount: entry.prize.amount,
    }
  })

  return {
    prizePool: {
      type: overallType,
      amounts,
    },
  }
}

export async function fetchLeaderboards() {
  try {
    const response = await api.getLeaderboards()
    leaderboards.value = response.leaderboards
  } catch (e) {
    console.error('[LiveOpsChallenge] Failed to fetch leaderboards:', e)
    leaderboards.value = []
  }
}

export { mapPrizePoolToChallengeFormat }

export const useLiveOpsChallenge = () => {
  const { now } = useNow()

  const activeLeaderboards = computed(() =>
    leaderboards.value.filter(lb => lb.endTimestamp > now.value),
  )

  const activeLeaderboard = computed(() =>
    activeLeaderboards.value[0] ?? null,
  )

  const challengeData = computed<Challenge>(() => {
    if (!activeLeaderboard.value) return null
    return mapPrizePoolToChallengeFormat(activeLeaderboard.value)
  })

  const leaderboardName = computed(() => activeLeaderboard.value?.name ?? '')
  const leaderboardId = computed(() => activeLeaderboard.value?.id ?? '')

  const countdown = computed(() => {
    if (!activeLeaderboard.value) return 0
    return Math.max(0, activeLeaderboard.value.endTimestamp - now.value)
  })

  const formattedCountdown = computed(() => formatDuration(
    countdown.value,
    undefined,
    countdown.value > 3600 * 72,
  ))

  const isLiveOpsChallengeLive = computed(() => activeLeaderboards.value.length > 0)

  const activeLeaderboardTemplate = computed(() => {
    if (!activeLeaderboard.value) return null
    return liveOpsEventTemplates.value.find(
      t => t.id === activeLeaderboard.value!.mainEventTemplateId,
    ) ?? null
  })

  const mainColor = computed(() => activeLeaderboardTemplate.value?.mainColor ?? '#635BFF')
  const mainIconUrl = computed(() => activeLeaderboardTemplate.value?.mainIconUrl ?? '')
  const mainIconBackgroundColor = computed(() => activeLeaderboardTemplate.value?.mainIconBackgroundColor ?? '#1D1D1B')

  return {
    activeLeaderboards,
    leaderboards,
    challengeData,
    formattedCountdown,
    countdown,
    leaderboardName,
    leaderboardId,
    isLiveOpsChallengeLive,
    mainColor,
    mainIconUrl,
    mainIconBackgroundColor,
    fetchLeaderboards,
  }
}
